<template>
  <!-- Modal backdrop -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        ref="modalContainer"
        class="fixed inset-0 z-50 overflow-y-auto"
        :aria-labelledby="titleId"
        :aria-describedby="contentId"
        :aria-label="accessibleLabel"
        role="dialog"
        aria-modal="true"
        @click="handleBackdropClick"
        @keydown="handleKeydown"
      >
        <!-- Backdrop overlay - Design System V2 -->
        <div
          class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
          :class="backdropClasses"
          aria-hidden="true"
        ></div>

        <!-- Modal container -->
        <div class="flex min-h-full items-center justify-center p-4">
          <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <div
              v-if="modelValue"
              :class="modalClasses"
              class="bg-white/95 backdrop-blur-xl"
              @click.stop
            >
              <!-- Close button with enhanced accessibility - Design System V2 -->
              <button
                v-if="closeable"
                ref="closeButton"
                type="button"
                class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 focus:outline-none focus:text-slate-600 transition-all duration-200 z-10 p-2 rounded-lg focus:ring-2 focus:ring-accent-500/30 focus:bg-accent-50 hover:bg-slate-100"
                :aria-label="closeAriaLabel"
                @click="handleClose"
                @keydown.enter="handleClose"
                @keydown.space.prevent="handleClose"
              >
                <svg
                  class="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <!-- Modal header -->
              <div
                v-if="$slots.header || title"
                class="modal-header"
                :class="headerClasses"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1 min-w-0">
                    <!-- Title with enhanced semantics - Design System V2 -->
                    <h2
                      v-if="title"
                      :id="titleId"
                      class="text-xl font-semibold text-slate-900 pr-8"
                      :aria-level="titleLevel"
                    >
                      {{ title }}
                    </h2>
                    <div
                      v-else-if="$slots.header"
                      :id="titleId"
                      role="heading"
                      :aria-level="titleLevel"
                    >
                      <slot name="header" />
                    </div>
                    
                    <!-- Subtitle -->
                    <p
                      v-if="subtitle"
                      class="mt-2 text-sm text-slate-600"
                    >
                      {{ subtitle }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Modal body -->
              <div
                v-if="$slots.default"
                :id="contentId"
                class="modal-body"
                :class="bodyClasses"
              >
                <slot />
              </div>

              <!-- Modal footer with enhanced navigation -->
              <div
                v-if="$slots.footer"
                class="modal-footer"
                :class="footerClasses"
                role="group"
                :aria-label="footerAriaLabel"
              >
                <slot name="footer" />
              </div>

              <!-- Loading overlay - Design System V2 -->
              <div
                v-if="loading"
                class="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center rounded-2xl"
              >
                <div class="flex items-center space-x-3">
                  <div class="animate-spin h-6 w-6 text-accent-600">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                  <span v-if="loadingText" class="text-slate-600">{{ loadingText }}</span>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, nextTick, onMounted, onUnmounted, watch, useSlots } from 'vue'

// Props definition
const props = defineProps({
  // v-model binding
  modelValue: {
    type: Boolean,
    default: false
  },
  
  // Modal size and appearance
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large', 'extra-large', 'full'].includes(value)
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'danger', 'success', 'warning'].includes(value)
  },
  
  // Modal content
  title: {
    type: String,
    default: null
  },
  subtitle: {
    type: String,
    default: null
  },
  
  // Modal behavior
  closeable: {
    type: Boolean,
    default: true
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true
  },
  closeOnEscape: {
    type: Boolean,
    default: true
  },
  persistent: {
    type: Boolean,
    default: false
  },
  
  // Loading state
  loading: {
    type: Boolean,
    default: false
  },
  loadingText: {
    type: String,
    default: 'Loading...'
  },
  
  // Enhanced accessibility
  closeAriaLabel: {
    type: String,
    default: 'Close modal'
  },
  accessibleLabel: {
    type: String,
    default: null
  },
  titleLevel: {
    type: [String, Number],
    default: 2,
    validator: (value) => [1, 2, 3, 4, 5, 6, '1', '2', '3', '4', '5', '6'].includes(value)
  },
  footerAriaLabel: {
    type: String,
    default: 'Modal actions'
  },
  trapFocus: {
    type: Boolean,
    default: true
  },
  
  // Custom classes
  modalClass: {
    type: [String, Array, Object],
    default: null
  },
  backdropClass: {
    type: [String, Array, Object],
    default: null
  }
})

// Events
const emit = defineEmits(['update:modelValue', 'close', 'open', 'backdrop-click', 'escape', 'focus-trapped'])

// Access slots
const slots = useSlots()

// Template refs
const modalContainer = ref(null)
const closeButton = ref(null)

// Unique IDs for accessibility
const titleId = ref(`modal-title-${Math.random().toString(36).substr(2, 9)}`)
const contentId = ref(`modal-content-${Math.random().toString(36).substr(2, 9)}`)

// Modal size classes - Design System V2
const modalClasses = computed(() => {
  const classes = [
    // Base styles with glassmorphism V3
    'relative rounded-2xl overflow-hidden transition-all duration-300',
    'max-h-[90vh] flex flex-col',
    'border border-slate-200 shadow-2xl',

    // Size variants
    {
      'w-full max-w-sm': props.size === 'small',
      'w-full max-w-md': props.size === 'medium',
      'w-full max-w-2xl': props.size === 'large',
      'w-full max-w-4xl': props.size === 'extra-large',
      'w-full h-full max-w-none max-h-none rounded-none': props.size === 'full'
    }
  ]

  // Variant-specific styling - accent colors
  switch (props.variant) {
    case 'danger':
      classes.push('border-t-4 border-t-red-500')
      break
    case 'success':
      classes.push('border-t-4 border-t-green-500')
      break
    case 'warning':
      classes.push('border-t-4 border-t-amber-500')
      break
    default:
      classes.push('border-t-4 border-t-accent-500')
  }

  // Custom classes
  if (props.modalClass) {
    classes.push(props.modalClass)
  }

  return classes
})

const backdropClasses = computed(() => {
  const classes = []
  
  // Custom backdrop classes
  if (props.backdropClass) {
    classes.push(props.backdropClass)
  }
  
  return classes
})

const headerClasses = computed(() => {
  const classes = ['px-6 py-4']

  if (slots.default || slots.footer) {
    classes.push('border-b border-slate-200')
  }

  return classes
})

const bodyClasses = computed(() => {
  const classes = ['px-6 py-4 flex-1 overflow-y-auto']

  // Variant-specific body styling - Design System V2
  switch (props.variant) {
    case 'danger':
      classes.push('text-red-800')
      break
    case 'success':
      classes.push('text-green-800')
      break
    case 'warning':
      classes.push('text-amber-800')
      break
    default:
      classes.push('text-slate-900')
  }

  return classes
})

const footerClasses = computed(() => {
  return [
    'px-6 py-4 bg-slate-50/50 backdrop-blur-sm border-t border-slate-200',
    'flex items-center justify-end space-x-3'
  ]
})

// Body scroll management
const lockBodyScroll = () => {
  document.body.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
  document.body.style.overflow = ''
}

// Event handlers
const handleClose = () => {
  if (props.persistent) return
  emit('update:modelValue', false)
  emit('close')
}

const handleBackdropClick = (event) => {
  emit('backdrop-click', event)
  
  if (props.closeOnBackdrop && !props.persistent) {
    handleClose()
  }
}

const handleEscape = (event) => {
  emit('escape', event)
  
  if (props.closeOnEscape && !props.persistent) {
    handleClose()
  }
}

// Enhanced focus management
let previouslyFocusedElement = null

const focusFirstElement = () => {
  nextTick(() => {
    const modal = modalContainer.value
    if (modal) {
      // Prioritize close button if variant is danger
      if (props.variant === 'danger' && closeButton.value) {
        closeButton.value.focus()
        return
      }

      const focusableElements = modal.querySelectorAll(
        'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
      )
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
      } else {
        // Focus the modal container itself if no focusable elements
        modal.focus()
      }
    }
  })
}

const storeFocus = () => {
  previouslyFocusedElement = document.activeElement
}

const restoreFocus = () => {
  if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
    // Ensure the element is still in the DOM and visible
    if (document.contains(previouslyFocusedElement)) {
      previouslyFocusedElement.focus()
    }
    previouslyFocusedElement = null
  }
}

// Enhanced keyboard navigation with focus trapping
const handleKeydown = (event) => {
  // Handle escape key
  if (event.key === 'Escape') {
    handleEscape(event)
    return
  }

  // Enhanced focus trapping
  if (event.key === 'Tab' && props.trapFocus) {
    const modal = modalContainer.value
    if (modal) {
      const focusableElements = modal.querySelectorAll(
        'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not([disabled])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (focusableElements.length === 0) {
        event.preventDefault()
        return
      }

      if (event.shiftKey) {
        if (document.activeElement === firstElement || !modal.contains(document.activeElement)) {
          event.preventDefault()
          lastElement.focus()
          emit('focus-trapped', { direction: 'backward', element: lastElement })
        }
      } else {
        if (document.activeElement === lastElement || !modal.contains(document.activeElement)) {
          event.preventDefault()
          firstElement.focus()
          emit('focus-trapped', { direction: 'forward', element: firstElement })
        }
      }
    }
  }
}

// Watch for modal open/close with enhanced accessibility
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    storeFocus()
    lockBodyScroll()
    focusFirstElement()
    document.addEventListener('keydown', handleKeydown)

    // Announce modal opening to screen readers
    nextTick(() => {
      if (modalContainer.value) {
        modalContainer.value.setAttribute('aria-hidden', 'false')
      }
    })

    emit('open')
  } else {
    unlockBodyScroll()
    document.removeEventListener('keydown', handleKeydown)
    restoreFocus()

    // Announce modal closing to screen readers
    if (modalContainer.value) {
      modalContainer.value.setAttribute('aria-hidden', 'true')
    }
  }
})

// Cleanup on unmount
onUnmounted(() => {
  unlockBodyScroll()
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Loading spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Ensure modal is above everything using design system z-index */
[role="dialog"] {
  z-index: var(--z-modal);
  /* Enhanced focus for screen readers */
  outline: none;
}

[role="dialog"]:focus {
  /* Subtle focus indicator for modal container */
  box-shadow: inset 0 0 0 2px rgba(20, 184, 166, 0.2);
}

/* Enhanced smooth transitions using design system */
.transition-opacity {
  transition: opacity var(--transition-base);
}

.transition-all {
  transition: all var(--transition-base);
  will-change: transform, opacity;
}

/* Transform utilities with GPU acceleration */
.scale-95 {
  --tw-scale-x: 0.95;
  --tw-scale-y: 0.95;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
  will-change: transform;
}

.translate-y-4 {
  --tw-translate-y: 1rem;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
  will-change: transform;
}

/* Enhanced close button styling */
.modal-close-button {
  will-change: transform, background-color;
}

/* Focus ring for close button */
.focus\:ring-3:focus {
  --tw-ring-width: 3px;
  --tw-ring-opacity: 0.3;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  [role="dialog"] {
    border: 3px solid #000000 !important;
    background: #ffffff !important;
  }

  [role="dialog"]:focus {
    box-shadow: inset 0 0 0 4px #000000;
  }

  button {
    border: 2px solid #000000 !important;
    font-weight: 600;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .transition-opacity,
  .transition-all {
    transition: none !important;
  }

  .scale-95,
  .translate-y-4 {
    transform: none !important;
    will-change: auto !important;
  }

  .modal-close-button {
    will-change: auto !important;
  }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
  button {
    min-height: 44px;
    min-width: 44px;
    padding: 0.75rem;
  }

  .modal-header {
    padding-right: 4rem; /* Extra space for larger touch targets */
  }
}

/* Screen reader optimizations */
@media (prefers-reduced-motion: reduce) {
  /* Ensure immediate focus for screen readers */
  [role="dialog"] {
    opacity: 1 !important;
    transform: none !important;
  }
}

/* Print styles */
@media print {
  [role="dialog"] {
    position: static !important;
    z-index: auto !important;
    background: white !important;
    color: black !important;
    border: 2px solid black !important;
    box-shadow: none !important;
    backdrop-filter: none !important;
  }

  .transition-opacity,
  .transition-all {
    transition: none !important;
  }

  button {
    display: none !important; /* Hide interactive elements in print */
  }

  .modal-footer {
    display: none !important;
  }
}
</style>